// @ts-check
import React, { useCallback, useEffect, useMemo, useState } from "react"
import Card from "../../../../../components/atoms/Card.atom"
import Button from "../../../../../components/atoms/Button.atom"
import { clsx } from "../../../../../utils/strings.utils"
import {
  ASK_QUESTIONS_CUSTOM_PLACEHOLDER,
  ASK_QUESTIONS_MAX_OUTPUT_CHARS,
} from "./askQuestions.constants"
import { buildAskQuestionsOutputString } from "./askQuestionsFormat"
import "../quick-v2/ApprovalDialog.css"

/**
 * @typedef {import("./askQuestionsFormat.js").AskQuestionDef} AskQuestionDef
 */

/**
 * @param {unknown} raw
 * @returns {AskQuestionDef[]}
 */
function parseQuestions(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return []
  const o = /** @type {Record<string, unknown>} */ (raw)
  const qs = o.questions
  if (!Array.isArray(qs)) return []
  /** @type {AskQuestionDef[]} */
  const out = []
  for (const item of qs) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue
    const q = /** @type {Record<string, unknown>} */ (item)
    const id = typeof q.id === "string" && q.id.trim() ? q.id.trim() : ""
    const question =
      typeof q.question === "string" && q.question.trim() ? q.question.trim() : ""
    if (!id || !question) continue
    const options = Array.isArray(q.options) ?
        q.options.filter((x) => typeof x === "string" && x.trim()).map((x) => x.trim())
      : []
    if (options.length === 0) continue
    out.push({
      id,
      question,
      options,
      multiple: Boolean(q.multiple),
    })
  }
  return out
}

/**
 * @param {AskQuestionDef} q
 * @param {import("./askQuestionsFormat.js").PerQuestionState | undefined} st
 */
function localSelectionsAndCustom(q, st) {
  if (st?.kind === "answered") {
    return {
      selections: [...st.selections],
      custom: st.custom,
    }
  }
  return { selections: [], custom: "" }
}

/**
 * @typedef {object} AskQuestionsCardProps
 * @property {import("ai").DynamicToolUIPart} part
 * @property {(output: string) => void | Promise<void>} onSubmit
 * @property {boolean} [busy]
 */

/**
 * @param {AskQuestionDef} q
 * @param {string[]} draftSelections
 * @param {string} draftCustom
 * @returns {import("./askQuestionsFormat.js").AnsweredState}
 */
function answeredFromDraft(q, draftSelections, draftCustom) {
  return {
    kind: "answered",
    selections: q.multiple ? [...draftSelections] : draftSelections[0] ?
        [draftSelections[0]]
      : [],
    custom: draftCustom,
  }
}

/**
 * @param {AskQuestionsCardProps} props
 */
export default function AskQuestionsCard({ part, onSubmit, busy = false }) {
  const questions = useMemo(
    () => parseQuestions("input" in part ? part.input : undefined),
    [part]
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [byId, setById] = useState(
    /** @type {Record<string, import("./askQuestionsFormat.js").PerQuestionState | undefined>} */ (
      {}
    )
  )
  const [draftSelections, setDraftSelections] = useState(/** @type {string[]} */ ([]))
  const [draftCustom, setDraftCustom] = useState("")
  const [lengthError, setLengthError] = useState(/** @type {string | null} */ (null))

  const toolCallId =
    "toolCallId" in part && typeof part.toolCallId === "string" ? part.toolCallId : ""

  useEffect(() => {
    setCurrentIndex(0)
    setById({})
    setDraftSelections([])
    setDraftCustom("")
    setLengthError(null)
  }, [toolCallId])

  const qLen = questions.length
  const current = questions[currentIndex]
  const isFirst = currentIndex <= 0
  const isLast = qLen > 0 && currentIndex >= qLen - 1
  const progress = qLen > 0 ? `${currentIndex + 1} / ${qLen}` : ""

  useEffect(() => {
    if (!current) return
    const st = byId[current.id]
    const { selections, custom } = localSelectionsAndCustom(current, st)
    setDraftSelections(selections)
    setDraftCustom(custom)
  }, [current, currentIndex, byId])

  const canProceedCurrent = useMemo(() => {
    if (!current) return false
    const hasOpt =
      current.multiple ?
        draftSelections.length > 0
      : Boolean(draftSelections[0]?.trim())
    const hasCustom = draftCustom.trim().length > 0
    return hasOpt || hasCustom
  }, [current, draftSelections, draftCustom])

  const emitIfWithinLimit = useCallback(
    /** @param {string} text */
    (text) => {
      if (text.length > ASK_QUESTIONS_MAX_OUTPUT_CHARS) {
        setLengthError(
          `El texto supera ${ASK_QUESTIONS_MAX_OUTPUT_CHARS} caracteres. Acorta las respuestas.`
        )
        return
      }
      setLengthError(null)
      onSubmit(text)
    },
    [onSubmit]
  )

  const handleSkipQuestion = useCallback(() => {
    if (!current || busy) return
    const next = {
      ...byId,
      [current.id]: /** @type {{ kind: 'skipped' }} */ ({
        kind: "skipped",
      }),
    }
    setById(next)
    if (!isLast) {
      setCurrentIndex((i) => i + 1)
    } else {
      const out = buildAskQuestionsOutputString(questions, next, "submit")
      emitIfWithinLimit(out)
    }
  }, [busy, byId, current, emitIfWithinLimit, isLast, questions])

  const handleSkipAll = useCallback(() => {
    if (busy) return
    const out = buildAskQuestionsOutputString(questions, byId, "skip-all")
    emitIfWithinLimit(out)
  }, [busy, byId, emitIfWithinLimit, questions])

  const handlePrev = useCallback(() => {
    if (isFirst || busy) return
    setCurrentIndex((i) => Math.max(0, i - 1))
    setLengthError(null)
  }, [busy, isFirst])

  const handleNext = useCallback(() => {
    if (!current || busy || !canProceedCurrent) return
    const answered = answeredFromDraft(current, draftSelections, draftCustom)
    const next = { ...byId, [current.id]: answered }
    setById(next)
    if (!isLast) {
      setCurrentIndex((i) => i + 1)
    } else {
      const out = buildAskQuestionsOutputString(questions, next, "submit")
      emitIfWithinLimit(out)
    }
  }, [
    busy,
    byId,
    canProceedCurrent,
    current,
    draftCustom,
    draftSelections,
    emitIfWithinLimit,
    isLast,
    questions,
  ])

  if (qLen === 0 || !current) return null

  /**
   * @param {string} opt
   */
  const toggleMulti = (opt) => {
    if (busy) return
    setDraftSelections((prev) =>
      prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]
    )
  }

  /**
   * @param {string} opt
   */
  const setSingle = (opt) => {
    if (busy) return
    setDraftSelections([opt])
  }

  return (
    <Card
      className={clsx(
        "approval-dialog estimation-agent-quick-v3__tool-approval-card estimation-agent-quick-v3__ask-questions-card"
      )}
    >
      <div className="approval-header">
        <i className="pi pi-comments approval-icon" aria-hidden />
        <h3 className="approval-title">Preguntas del asistente</h3>
      </div>

      <p className="approval-description">
        Responde para continuar la estimación · Pregunta {progress}
      </p>

      <div className="approval-content estimation-agent-quick-v3__ask-questions-body">
        <h4 className="estimation-agent-quick-v3__ask-questions-q">{current.question}</h4>

        <div
          className="estimation-agent-quick-v3__ask-questions-options"
          role={current.multiple ? "group" : "radiogroup"}
          aria-label={current.question}
        >
          {current.options.map((opt) =>
            current.multiple ?
              <label
                key={opt}
                className={clsx(
                  "estimation-agent-quick-v3__ask-questions-opt",
                  draftSelections.includes(opt) && "estimation-agent-quick-v3__ask-questions-opt--selected"
                )}
              >
                <input
                  type="checkbox"
                  checked={draftSelections.includes(opt)}
                  onChange={() => toggleMulti(opt)}
                  disabled={busy}
                />
                <span>{opt}</span>
              </label>
            : <label
                key={opt}
                className={clsx(
                  "estimation-agent-quick-v3__ask-questions-opt",
                  draftSelections[0] === opt &&
                    "estimation-agent-quick-v3__ask-questions-opt--selected"
                )}
              >
                <input
                  type="radio"
                  name={`ask-q-${toolCallId}-${current.id}`}
                  checked={draftSelections[0] === opt}
                  onChange={() => setSingle(opt)}
                  disabled={busy}
                />
                <span>{opt}</span>
              </label>
          )}
        </div>

        <div className="estimation-agent-quick-v3__ask-questions-custom">
          <textarea
            id={`ask-custom-${toolCallId}-${current.id}`}
            className="estimation-agent-quick-v3__ask-questions-textarea"
            rows={2}
            placeholder={ASK_QUESTIONS_CUSTOM_PLACEHOLDER}
            aria-label={ASK_QUESTIONS_CUSTOM_PLACEHOLDER}
            value={draftCustom}
            onChange={(e) => setDraftCustom(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      {lengthError ?
        <p className="estimation-agent-quick-v3__ask-questions-error" role="alert">
          {lengthError}
        </p>
      : null}

      <div className="approval-actions estimation-agent-quick-v3__ask-questions-actions">
        <div className="estimation-agent-quick-v3__ask-questions-actions-row">
          <Button
            type="button"
            label="Saltar todas"
            className="p-button-sm p-button-text p-button-secondary"
            onClick={handleSkipAll}
            disabled={busy}
          />
        </div>
        <div className="estimation-agent-quick-v3__ask-questions-actions-row estimation-agent-quick-v3__ask-questions-actions-nav">
          <Button
            type="button"
            icon="pi pi-chevron-left"
            className="p-button-sm p-button-text p-button-secondary"
            onClick={handlePrev}
            disabled={isFirst || busy}
            aria-label="Anterior"
          />
          <Button
            type="button"
            label="Saltar pregunta"
            className="p-button-sm p-button-text p-button-secondary"
            onClick={handleSkipQuestion}
            disabled={busy}
          />
          {isLast ?
            <Button
              type="button"
              label="Enviar respuestas"
              icon="pi pi-check"
              className="p-button-sm p-button-success"
              onClick={handleNext}
              disabled={busy || !canProceedCurrent}
              loading={busy}
            />
          : <Button
              type="button"
              label="Siguiente"
              icon="pi pi-chevron-right"
              iconPos="right"
              className="p-button-sm p-button-primary"
              onClick={handleNext}
              disabled={busy || !canProceedCurrent}
            />
          }
        </div>
      </div>
    </Card>
  )
}
